// Classic chart patterns (research spec A11), with the geometry exactly as tested, so the chart labels
// what was measured. Swing points come from an ATR zigzag (a reversal of 2 x ATR(14)) and each is usable
// only from the candle that confirms it; a pattern is the shape of the latest confirmed swing points, and
// it breaks out on the first close through its level within 30 candles of its last swing point.
//   double bottom / top: two lows within 3%, >= 10 candles apart, the peak between >= 10% above them;
//     level = that peak.
//   inverse / head & shoulders: the head beyond both shoulders, the shoulders within 5%; level = the
//     neckline through the two peaks between them.
//   falling / rising wedge: the last two swing highs and lows moving the same way and converging;
//     level = the line through the two highs (lows for a rising wedge).
//   bull / bear flag: a pole of >= 15% in <= 20 candles, then a pullback of 3-50% of it lasting >= 3
//     candles; level = the pole's end.
//   bullish / bearish pennant: the same pole, then a lower high and a higher low; level = the line from
//     the pole's end through the lower high.
//   ascending / descending triangle: two highs within 2% and two rising lows; level = the higher high.
// Target = the measured move; stop = beyond the pattern's last extreme by 0.25 ATR (what the test used).
import { atrSeries, type Day } from './setups';

export type PatternName =
  | 'double bottom' | 'double top' | 'inverse head & shoulders' | 'head & shoulders' | 'falling wedge' | 'rising wedge'
  | 'bull flag' | 'bear flag' | 'bullish pennant' | 'bearish pennant' | 'ascending triangle' | 'descending triangle';

/** A pattern found in the candles: its swing points, its breakout level and, if it broke, where. */
export interface ChartPattern {
  name: PatternName;
  bull: boolean;
  /** The swing points that make the shape (candle index and price), oldest first. */
  points: { i: number; p: number }[];
  /** The breakout level from the first swing point it applies to until the breakout (or the latest candle). */
  level: { from: { i: number; p: number }; to: { i: number; p: number } };
  /** The candle whose close went through the level, or null while it is still forming. */
  breakIdx: number | null;
  target: number;
  stop: number;
}

/** What MoneyHQ's test found for each pattern (Nov 2018 - Oct 2026, 2,921 NSE stocks including delisted
 * ones, daily candles, after 0.35% costs). Bullish ones were traded long to the measured-move target or
 * the stop (40 sessions at most); bearish ones were judged as warnings: the stock against the NIFTY 500
 * over the next 20 sessions. `bp` is that excess return, `placebo` the same rule on random dates of the
 * same stock; `edge` the reading. */
export const PATTERN_RECORD: Record<PatternName, { n: number; bp: number; placebo: number; edge: 'hint' | 'none' | 'worse' | 'few' }> = {
  'bull flag': { n: 783, bp: 68, placebo: 7, edge: 'hint' },
  'bullish pennant': { n: 31, bp: 112, placebo: 175, edge: 'few' },
  'double bottom': { n: 1462, bp: -45, placebo: 8, edge: 'worse' },
  'inverse head & shoulders': { n: 1041, bp: -18, placebo: -28, edge: 'none' },
  'falling wedge': { n: 2491, bp: -20, placebo: -43, edge: 'none' },
  'ascending triangle': { n: 562, bp: -110, placebo: -16, edge: 'worse' },
  'bear flag': { n: 554, bp: 132, placebo: 5, edge: 'worse' },
  'bearish pennant': { n: 34, bp: -81, placebo: -113, edge: 'few' },
  'double top': { n: 1574, bp: 52, placebo: 20, edge: 'none' },
  'head & shoulders': { n: 1392, bp: -36, placebo: 15, edge: 'hint' },
  'rising wedge': { n: 3439, bp: 62, placebo: 17, edge: 'none' },
  'descending triangle': { n: 971, bp: 7, placebo: -15, edge: 'none' },
};

export const PATTERN_ZIGZAG_ATR = 2;
export const PATTERN_WINDOW = 30;

interface Piv { t: 'H' | 'L'; j: number; p: number; conf: number }

/** Swing points: a high (low) once the close has fallen (risen) mult x ATR(14) from it. */
export function zigzag(d: Day[], mult = PATTERN_ZIGZAG_ATR): Piv[] {
  if (!d.length) return [];
  const a = atrSeries(d, 14), out: Piv[] = [];
  let dir = 0, hi = d[0].h, hiJ = 0, lo = d[0].l, loJ = 0;
  for (let j = 1; j < d.length; j++) {
    const th = mult * (a[j] || Infinity);
    if (d[j].h > hi) { hi = d[j].h; hiJ = j; }
    if (d[j].l < lo) { lo = d[j].l; loJ = j; }
    if (dir >= 0 && hiJ < j && hi - d[j].c >= th) { out.push({ t: 'H', j: hiJ, p: hi, conf: j }); dir = -1; lo = d[j].l; loJ = j; }
    else if (dir <= 0 && loJ < j && d[j].c - lo >= th) { out.push({ t: 'L', j: loJ, p: lo, conf: j }); dir = 1; hi = d[j].h; hiJ = j; }
  }
  return out;
}

const line = (a: Piv, b: Piv) => (j: number) => a.p + ((b.p - a.p) * (j - a.j)) / (b.j - a.j);
interface Shape { name: PatternName; bull: boolean; pts: Piv[]; level: (j: number) => number; from: number; stop: number; target: number; last: number; key: string }

/** The shapes the latest confirmed swing points make (pv oldest first). */
function shapes(pv: Piv[], A: number): Shape[] {
  const out: Shape[] = [], n = pv.length, P = (k: number) => pv[n - k];
  for (const bull of [true, false]) {
    const lowT = bull ? 'L' : 'H', highT = bull ? 'H' : 'L', sg = bull ? 1 : -1;
    if (n >= 3 && P(1).t === lowT && P(2).t === highT && P(3).t === lowT) {
      const L1 = P(3), Hp = P(2), L2 = P(1), ext = bull ? Math.min(L1.p, L2.p) : Math.max(L1.p, L2.p);
      if (Math.abs(L2.p - L1.p) / L1.p <= 0.03 && L2.j - L1.j >= 10 && sg * (Hp.p - ext) / ext >= 0.1)
        out.push({ name: bull ? 'double bottom' : 'double top', bull, pts: [L1, Hp, L2], level: () => Hp.p, from: Hp.j, stop: ext - sg * 0.25 * A, target: Hp.p + (Hp.p - ext), last: L2.j, key: `db${L1.j}` });
    }
    if (n >= 5 && P(1).t === lowT && P(3).t === lowT && P(5).t === lowT) {
      const LS = P(5), H1 = P(4), HD = P(3), H2 = P(2), RS = P(1);
      if (sg * (LS.p - HD.p) > 0 && sg * (RS.p - HD.p) > 0 && Math.abs(LS.p - RS.p) / LS.p <= 0.05) {
        const nl = line(H1, H2);
        out.push({ name: bull ? 'inverse head & shoulders' : 'head & shoulders', bull, pts: [LS, H1, HD, H2, RS], level: nl, from: H1.j, stop: RS.p - sg * 0.25 * A, target: nl(HD.j) + (nl(HD.j) - HD.p), last: RS.j, key: `hs${LS.j}` });
      }
    }
    if (n >= 4) {
      const hs = pv.slice(-6).filter((x) => x.t === 'H').slice(-2), ls = pv.slice(-6).filter((x) => x.t === 'L').slice(-2);
      if (hs.length === 2 && ls.length === 2) {
        const sH = (hs[1].p - hs[0].p) / (hs[1].j - hs[0].j), sL = (ls[1].p - ls[0].p) / (ls[1].j - ls[0].j);
        const startW = Math.abs(hs[0].p - ls[0].p), endW = Math.abs(hs[1].p - ls[1].p);
        if (bull ? sH < 0 && sL < 0 && sH < sL && endW < startW : sH > 0 && sL > 0 && sL > sH && endW < startW) {
          const edge = bull ? hs : ls;
          out.push({ name: bull ? 'falling wedge' : 'rising wedge', bull, pts: [...hs, ...ls].sort((x, y) => x.j - y.j), level: line(edge[0], edge[1]), from: edge[0].j, stop: (bull ? ls[1].p : hs[1].p) - sg * 0.25 * A, target: bull ? hs[0].p : ls[0].p, last: Math.max(hs[1].j, ls[1].j), key: `w${hs[0].j}` });
        }
      }
    }
    if (n >= 3 && P(2).t === highT && P(3).t === lowT) {
      const base = P(3), top = P(2), pole = sg * (top.p - base.p) / base.p, fl = P(1);
      if (pole >= 0.15 && top.j - base.j <= 20) {
        const depth = (top.p - fl.p) / (top.p - base.p);
        if (fl.t === lowT && depth >= 0.03 && depth <= 0.5 && fl.j - top.j >= 3)
          out.push({ name: bull ? 'bull flag' : 'bear flag', bull, pts: [base, top, fl], level: () => top.p, from: top.j, stop: fl.p - sg * 0.25 * A, target: top.p + sg * Math.abs(top.p - base.p), last: fl.j, key: `f${top.j}` });
      }
    }
    if (n >= 5 && P(4).t === highT && P(5).t === lowT) {
      const base = P(5), top = P(4), l1 = P(3), h2 = P(2), l2 = P(1), pole = sg * (top.p - base.p) / base.p;
      if (pole >= 0.15 && top.j - base.j <= 20 && l1.t === lowT && h2.t === highT && l2.t === lowT && sg * (top.p - h2.p) > 0 && sg * (l2.p - l1.p) > 0 && sg * (top.p - l1.p) / Math.abs(top.p - base.p) <= 0.5)
        out.push({ name: bull ? 'bullish pennant' : 'bearish pennant', bull, pts: [base, top, l1, h2, l2], level: line(top, h2), from: top.j, stop: l2.p - sg * 0.25 * A, target: top.p + sg * Math.abs(top.p - base.p), last: l2.j, key: `p${top.j}` });
    }
    if (n >= 4) {
      const hs = pv.slice(-5).filter((x) => x.t === highT).slice(-2), ls = pv.slice(-5).filter((x) => x.t === lowT).slice(-2);
      if (hs.length === 2 && ls.length === 2 && Math.abs(hs[1].p - hs[0].p) / hs[0].p <= 0.02 && sg * (ls[1].p - ls[0].p) > 0 && ls[0].j > hs[0].j - 30) {
        const R = bull ? Math.max(hs[0].p, hs[1].p) : Math.min(hs[0].p, hs[1].p);
        out.push({ name: bull ? 'ascending triangle' : 'descending triangle', bull, pts: [...hs, ...ls].sort((x, y) => x.j - y.j), level: () => R, from: hs[0].j, stop: ls[1].p - sg * 0.25 * A, target: R + (R - ls[0].p), last: Math.max(hs[1].j, ls[1].j), key: `t${hs[0].j}` });
      }
    }
  }
  return out;
}

/**
 * Every pattern that broke out in the candles, plus those still forming at the latest candle (shape in
 * place, no breakout yet, within the 30-candle window, the close not past its stop). Causal: each candle sees only swing points
 * confirmed by then, as in the test (which also needed a year of history and a liquid stock; this
 * does not filter for those).
 */
export function chartPatterns(d: Day[]): ChartPattern[] {
  const pv = zigzag(d), a = atrSeries(d, 14), used = new Set<string>(), out: ChartPattern[] = [];
  const confirmed: Piv[] = [];
  let q = 0, live: Shape[] = [];
  const toPattern = (s: Shape, end: number, breakIdx: number | null): ChartPattern => ({
    name: s.name, bull: s.bull, points: s.pts.map((x) => ({ i: x.j, p: x.p })),
    level: { from: { i: s.from, p: s.level(s.from) }, to: { i: end, p: s.level(end) } },
    breakIdx, target: s.target, stop: s.stop,
  });
  for (let t = 1; t < d.length; t++) {
    while (q < pv.length && pv[q].conf <= t) confirmed.push(pv[q++]);
    if (confirmed.length < 3) continue;
    live = shapes(confirmed, a[t] || 0).filter((s) => !used.has(s.name + s.key) && t - s.last <= PATTERN_WINDOW && t > s.last);
    for (const s of live) {
      const lv = s.level(t), lvPrev = s.level(t - 1);
      const crossed = s.bull ? d[t].c > lv && d[t - 1].c <= lvPrev : d[t].c < lv && d[t - 1].c >= lvPrev;
      if (!crossed) continue;
      used.add(s.name + s.key);
      out.push(toPattern(s, t, t));
    }
  }
  const lastT = d.length - 1;
  // Still forming: the shape is in place and the latest close is between its stop and its level (past
  // the stop, the shape has failed).
  const c = d[lastT]?.c;
  for (const s of live) if (!used.has(s.name + s.key) && (s.bull ? c > s.stop : c < s.stop)) out.push(toPattern(s, lastT, null));
  return out.sort((x, y) => (x.breakIdx ?? lastT + 1) - (y.breakIdx ?? lastT + 1));
}

export const BEARISH_PATTERNS = new Set<PatternName>(['double top', 'head & shoulders', 'rising wedge', 'bear flag', 'bearish pennant', 'descending triangle']);

/** Plain-language reading of a pattern's tested record. */
export function patternVerdict(name: PatternName): string {
  const r = PATTERN_RECORD[name], bull = !BEARISH_PATTERNS.has(name);
  const pct = (bp: number) => `${bp >= 0 ? '+' : '−'}${(Math.abs(bp) / 100).toFixed(2)}%`;
  const what = bull ? 'a trade vs the NIFTY 500' : 'the next 20 sessions vs the NIFTY 500';
  const base = `${pct(r.bp)} ${what}, random dates ${pct(r.placebo)}, ${r.n.toLocaleString('en-IN')} cases`;
  if (r.edge === 'few') return `too few cases to judge (${base})`;
  if (r.edge === 'worse') return `${bull ? 'did worse than random dates' : 'the stock usually did better afterwards, not worse'} (${base})`;
  if (r.edge === 'hint') return `a weak hint, not statistically solid (${base})`;
  return `no edge over random dates (${base})`;
}
